"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import cn from "twsx";
import type { HorizontalScrollRegionSelectors } from "./useHorizontalScrollRegion";
import { useHorizontalScrollRegion } from "./useHorizontalScrollRegion";
type ScrollAreaOrientation = "vertical" | "horizontal" | "both";
type ScrollAreaType = "auto" | "always" | "hover" | "scroll";
type DataAttributes = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};
type ScrollBarProps = React.ComponentPropsWithoutRef<"div"> & {
  orientation?: "vertical" | "horizontal";
};
type ScrollAreaProps = React.ComponentPropsWithoutRef<"div"> & {
  orientation?: ScrollAreaOrientation;
  type?: ScrollAreaType;
  viewportClassName?: string;
  viewportProps?: Omit<React.ComponentPropsWithoutRef<"div">, "children" | "className" | "ref"> & DataAttributes;
  viewportRef?: React.Ref<HTMLDivElement>;
  horizontalScrollbarRegion?: HorizontalScrollRegionSelectors;
};
type MutableRefObject<T> = {
  current: T;
};
function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  if (ref) {
    (ref as MutableRefObject<T | null>).current = value;
  }
}
const ScrollBar = React.forwardRef<HTMLDivElement, ScrollBarProps>((t0, ref) => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      orientation: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const orientation = t1 === undefined ? "vertical" : t1;
  const t2 = orientation === "vertical" ? "right-0 top-0 h-full w-scrollbar" : "bottom-0 left-0 h-scrollbar w-full";
  let t3;
  if ($[4] !== className || $[5] !== t2) {
    t3 = cn("pointer-events-none absolute opacity-0", t2, className);
    $[4] = className;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== ref || $[9] !== t3) {
    t4 = <div ref={ref} aria-hidden="true" className={t3} {...props} />;
    $[7] = props;
    $[8] = ref;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
});

// React 19 and the current Radix ScrollArea build can loop via callback refs.
// Keep the public Atlas wrapper API stable with a native-scroll implementation
// until the dependency can be upgraded safely.
/** Use when a fixed-height region scrolls its own content. */
const ScrollArea = React.forwardRef<HTMLDivElement, ScrollAreaProps>((t0, ref) => {
  const $ = _c(46);
  let children;
  let className;
  let horizontalScrollbarRegion;
  let props;
  let t1;
  let t2;
  let viewportClassName;
  let viewportProps;
  let viewportRef;
  if ($[0] !== t0) {
    ({
      className,
      children,
      viewportClassName,
      viewportProps,
      viewportRef,
      horizontalScrollbarRegion,
      orientation: t1,
      type: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = horizontalScrollbarRegion;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = viewportClassName;
    $[8] = viewportProps;
    $[9] = viewportRef;
  } else {
    children = $[1];
    className = $[2];
    horizontalScrollbarRegion = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    viewportClassName = $[7];
    viewportProps = $[8];
    viewportRef = $[9];
  }
  const orientation = t1 === undefined ? "vertical" : t1;
  const type = t2 === undefined ? "scroll" : t2;
  const internalViewportRef = React.useRef(null);
  const horizontalProxyRef = React.useRef(null);
  const region = useHorizontalScrollRegion(internalViewportRef, horizontalProxyRef, horizontalScrollbarRegion);
  let t3;
  if ($[10] !== viewportRef) {
    t3 = node => {
      internalViewportRef.current = node;
      assignRef(viewportRef, node);
    };
    $[10] = viewportRef;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const mergedViewportRef = t3;
  let t4;
  if ($[12] !== horizontalScrollbarRegion || $[13] !== region || $[14] !== viewportProps?.style) {
    t4 = horizontalScrollbarRegion ? {
      ...viewportProps?.style,
      height: `calc(100% + ${region.clipHeight}px)`
    } : viewportProps?.style;
    $[12] = horizontalScrollbarRegion;
    $[13] = region;
    $[14] = viewportProps?.style;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = orientation !== "horizontal" && "[scrollbar-gutter:stable]";
  const t6 = orientation === "vertical" && "overflow-y-auto overflow-x-hidden";
  const t7 = orientation === "horizontal" && "overflow-x-auto overflow-y-hidden";
  const t8 = orientation === "both" && "overflow-auto";
  let t9;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== viewportClassName) {
    t9 = cn("iebridge-scrollbar min-h-0 min-w-0 h-full w-full rounded-inherit", "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent", t5, t6, t7, t8, viewportClassName);
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = viewportClassName;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== children || $[23] !== horizontalScrollbarRegion || $[24] !== region) {
    t10 = horizontalScrollbarRegion ? <div style={{
      paddingBottom: region.paddingBottom
    }}>{children}</div> : children;
    $[22] = children;
    $[23] = horizontalScrollbarRegion;
    $[24] = region;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== mergedViewportRef || $[27] !== orientation || $[28] !== t10 || $[29] !== t4 || $[30] !== t9 || $[31] !== viewportProps) {
    t11 = <div ref={mergedViewportRef} role="region" aria-label="Scrollable content" tabIndex={0} data-scroll-area-viewport={orientation} {...viewportProps} style={t4} className={t9}>{t10}</div>;
    $[26] = mergedViewportRef;
    $[27] = orientation;
    $[28] = t10;
    $[29] = t4;
    $[30] = t9;
    $[31] = viewportProps;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const viewport = t11;
  const t12 = horizontalScrollbarRegion && "flex flex-col";
  let t13;
  if ($[33] !== className || $[34] !== t12) {
    t13 = cn("relative overflow-hidden", className, t12);
    $[33] = className;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== horizontalScrollbarRegion || $[37] !== region || $[38] !== viewport) {
    t14 = horizontalScrollbarRegion ? <><div className="min-h-0 flex-1 overflow-hidden">{viewport}</div><div ref={horizontalProxyRef} tabIndex={region.maxScrollLeft > 0 && region.trackWidth > 0 ? 0 : -1} role="region" aria-label="Scroll horizontally" className={cn("iebridge-scrollbar shrink-0 overflow-x-scroll overflow-y-hidden", "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent")} style={{
        marginLeft: region.start,
        width: region.trackWidth,
        height: region.maxScrollLeft > 0 && region.trackWidth > 0 ? region.clipHeight : 0
      }}><div style={{
          width: region.contentWidth,
          height: 1
        }} /></div></> : viewport;
    $[36] = horizontalScrollbarRegion;
    $[37] = region;
    $[38] = viewport;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== props || $[41] !== ref || $[42] !== t13 || $[43] !== t14 || $[44] !== type) {
    t15 = <div ref={ref} data-scroll-area-type={type} className={t13} {...props}>{t14}</div>;
    $[40] = props;
    $[41] = ref;
    $[42] = t13;
    $[43] = t14;
    $[44] = type;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
});
ScrollBar.displayName = "ScrollBar";
ScrollArea.displayName = "ScrollArea";
export { ScrollArea, ScrollBar };